// components/bids/documents/ExtractionResultModal.jsx

/**
 * ExtractionResultModal
 * Displays structured data extracted from a proposal PDF via Gemini.
 * When engagementId is provided, shows an "Apply to Engagement" button
 * that writes extracted fields back to the engagement record.
 */

import React, { useState } from 'react';
import {
  XMarkIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { documentsApi } from '../../../hooks/document/documentsApi';

// ── Field label map for display in success confirmation ─────────────────────
const FIELD_LABELS = {
  bid_amount:      'Bid Amount',
  scope_summary:   'Scope Summary',
  engagement_type: 'Engagement Type',
  start_date:      'Start Date',
  practice_area:   'Practice Area',
  industry:        'Industry',
  tags:            'Tags',
};

const ExtractionResultModal = ({ result, engagementId, onClose, onApplied }) => {
  const [applyState, setApplyState] = useState('idle'); // idle | loading | success | error
  const [appliedFields, setAppliedFields] = useState([]);
  const [applyError, setApplyError] = useState(null);

  if (!result) return null;

  const { status, confidence_score, extraction, tags, pages_processed, processing_time_seconds } = result;

  // Support both shapes: result from fresh extraction vs result from GET /extraction
  // Fresh extraction: result.extraction, result.tags
  // Stored retrieval: result.extraction_result.extraction, result.extraction_result.tags
  const extractionData = extraction ?? result?.extraction_result?.extraction ?? null;
  const tagsData = tags ?? result?.extraction_result?.tags ?? null;

  const confidencePct = Math.round((confidence_score || 0) * 100);
  const confidenceColor =
    confidencePct >= 75 ? 'text-teal-700 bg-teal-50 border-teal-200' :
    confidencePct >= 50 ? 'text-yellow-700 bg-yellow-50 border-yellow-200' :
                          'text-pink-700 bg-pink-50 border-pink-200';

  const hasEngagement = Boolean(engagementId);

  const handleApply = async () => {
    if (!hasEngagement || applyState === 'loading') return;
    setApplyState('loading');
    setApplyError(null);
    try {
      const documentId = result.document_id;
      if (!documentId) throw new Error('No document ID on result');

      const response = await documentsApi.applyExtractionToEngagement(documentId, engagementId);
      const data = response?.data ?? response;

      setAppliedFields(data?.applied_fields || []);
      setApplyState('success');
      if (onApplied) onApplied();
    } catch (err) {
      console.error('Apply extraction failed:', err);
      setApplyError(err?.response?.data?.detail || err.message || 'Failed to apply extraction.');
      setApplyState('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 bg-grey-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-darkblue-500">AI Extraction Result</h3>
              <p className="text-xs text-grey-500">
                {pages_processed} page{pages_processed !== 1 ? 's' : ''} processed
                {processing_time_seconds ? ` · ${processing_time_seconds.toFixed(1)}s` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${confidenceColor}`}>
              {confidencePct}% confidence
            </span>
            <button
              onClick={onClose}
              className="p-2 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status banner */}
        {status === 'failed' ? (
          <div className="mx-6 mt-4 p-3 bg-pink-50 border border-pink-200 rounded-lg flex items-center gap-2 text-pink-700 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            Extraction failed. The document may be scanned or have no extractable text.
          </div>
        ) : status === 'partial' ? (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-700 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            Partial extraction — some fields could not be identified.
          </div>
        ) : (
          <div className="mx-6 mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2 text-teal-700 text-sm">
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            Extraction successful.
          </div>
        )}

        {/* Apply success banner */}
        {applyState === 'success' && appliedFields.length > 0 && (
          <div className="mx-6 mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <div className="flex items-center gap-2 text-teal-700 text-sm font-medium mb-2">
              <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
              Applied to engagement
            </div>
            <div className="flex flex-wrap gap-1.5">
              {appliedFields.map(f => (
                <span
                  key={f}
                  className="px-2 py-0.5 bg-teal-100 text-teal-700 border border-teal-200 rounded-full text-xs font-medium"
                >
                  {FIELD_LABELS[f] || f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Apply error banner */}
        {applyState === 'error' && applyError && (
          <div className="mx-6 mt-3 p-3 bg-pink-50 border border-pink-200 rounded-lg flex items-start gap-2 text-pink-700 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{applyError}</span>
          </div>
        )}

        {/* Body */}
        {extractionData && (
          <div className="p-6 space-y-5">

            {/* Client + Proposal */}
            <Section icon={BuildingOfficeIcon} title="Client & Proposal">
              <Grid>
                <Field label="Client" value={extractionData.client_name} />
                <Field label="Service" value={extractionData.service_category} />
                <Field label="Engagement Type" value={extractionData.engagement_type} />
                <Field label="Proposal Date" value={extractionData.proposal_date} />
                <Field label="Agreement Date" value={extractionData.agreement_date} />
                <Field label="Signed by Client" value={extractionData.signed_by_client} />
              </Grid>
            </Section>

            {/* Financials */}
            <Section icon={CurrencyDollarIcon} title="Financials">
              <Grid>
                <Field
                  label="Total Fee"
                  value={extractionData.total_fee != null
                    ? `$${Number(extractionData.total_fee).toLocaleString()}`
                    : null}
                />
                <Field label="Fee Type" value={extractionData.fee_type} />
                <Field label="Payment Terms" value={extractionData.payment_terms} />
                <Field label="Late Fee" value={extractionData.late_fee_rate != null ? `${extractionData.late_fee_rate}%/mo` : null} />
                <Field label="CC Fee" value={extractionData.credit_card_fee != null ? `${extractionData.credit_card_fee}%` : null} />
                <Field label="Termination Notice" value={extractionData.termination_notice_days != null ? `${extractionData.termination_notice_days} days` : null} />
              </Grid>
            </Section>

            {/* Timeline */}
            <Section icon={CalendarIcon} title="Timeline">
              <Grid>
                <Field label="Duration" value={extractionData.timeline_weeks != null ? `${extractionData.timeline_weeks} weeks` : null} />
                <Field label="Start Date" value={extractionData.start_date} />
                <Field label="Timeline Notes" value={extractionData.timeline_description} span={2} />
              </Grid>
            </Section>

            {/* Scope */}
            <Section icon={DocumentTextIcon} title="Scope">
              {extractionData.goals && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">Goals</p>
                  <p className="text-sm text-darkblue-500">{extractionData.goals}</p>
                </div>
              )}
              {extractionData.scope_summary && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">Summary</p>
                  {/* Render as-is — may contain bullet points using • or - */}
                  <div className="text-sm text-darkblue-500 whitespace-pre-line leading-relaxed">
                    {extractionData.scope_summary}
                  </div>
                </div>
              )}
              {extractionData.services?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">Services</p>
                  <ul className="space-y-1.5">
                    {extractionData.services.map((s, i) => (
                      <li key={i} className="text-sm text-darkblue-500 flex items-start gap-2">
                        <span className="text-brand-500 mt-0.5 shrink-0">•</span>
                        <span>
                          <span className="font-medium">{s.name}</span>
                          {s.description && <span className="text-grey-500"> — {s.description}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* People */}
            {extractionData.spokespeople?.length > 0 && (
              <Section icon={UserIcon} title="Spokespeople">
                <div className="flex flex-wrap gap-2">
                  {extractionData.spokespeople.map((name, i) => (
                    <span key={i} className="px-2.5 py-1 bg-brightblue-50 text-brightblue-700 rounded-full text-sm font-medium">
                      {name}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Tags */}
            {tagsData?.tags?.length > 0 && (
              <Section icon={TagIcon} title="Auto-generated Tags">
                <div className="flex flex-wrap gap-2">
                  {tagsData.tags.map((tag, i) => (
                    <span key={i} className="px-2.5 py-1 bg-brand-50 text-brand-600 border border-brand-200 rounded-full text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                {tagsData.industry && (
                  <p className="text-xs text-grey-500 mt-2">Industry: {tagsData.industry}</p>
                )}
              </Section>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 rounded-b-xl flex items-center justify-between gap-3">

          {/* Apply to Engagement */}
          <div className="relative group">
            <button
              onClick={hasEngagement ? handleApply : undefined}
              disabled={!hasEngagement || applyState === 'loading' || applyState === 'success'}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${hasEngagement && applyState !== 'success'
                  ? 'bg-darkblue-500 text-white hover:bg-darkblue-600 disabled:opacity-50'
                  : 'bg-grey-100 text-grey-400 cursor-not-allowed'
                }
              `}
            >
              {applyState === 'loading' ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
              ) : applyState === 'success' ? (
                <CheckCircleIcon className="w-4 h-4 text-teal-500" />
              ) : (
                <LinkIcon className="w-4 h-4" />
              )}
              {applyState === 'success' ? 'Applied' : 'Apply to Engagement'}
            </button>

            {/* Tooltip — only shown when no engagement is linked */}
            {!hasEngagement && (
              <div className="absolute bottom-full left-0 mb-2 px-2.5 py-1.5 bg-darkblue-500 text-white text-xs rounded-lg whitespace-nowrap z-20 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Link an engagement first
                <div className="absolute top-full left-4 border-4 border-transparent border-t-darkblue-500" />
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2 bg-grey-100 text-grey-700 rounded-lg hover:bg-grey-200 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-grey-400" />
      <h4 className="text-sm font-semibold text-darkblue-500">{title}</h4>
    </div>
    <div className="pl-6">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
    {children}
  </div>
);

const Field = ({ label, value, span = 1 }) => {
  if (!value) return null;
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <p className="text-xs text-grey-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-sm text-darkblue-500 mt-0.5">{value}</p>
    </div>
  );
};

export default ExtractionResultModal;